import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { ReactElement } from "react";
import { Tooltip } from "@/components/ui/tooltip-card";
import { useTheme } from "@mui/material";
import { useStoredTheme } from "@/utils/theme-context";

interface ButtonGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    icon?: ReactElement;
    tooltip?: string | React.ReactNode;
  }>;
  className?: string;
  size?: "sm" | "default" | "lg";
  disabled?: boolean;
}

export function ButtonGroup({
  value,
  onValueChange,
  options,
  className,
  size = "default",
  disabled = false,
}: ButtonGroupProps) {
  const storedTheme = useStoredTheme();
  const theme = useTheme();

  return (
    <div
      className={cn(
        "grid grid-cols-2 bg-muted rounded-[8px] p-[2px]",
        className,
      )}
      style={{
        backgroundColor:
          storedTheme.button.secondary.backgroundColor[theme.palette.mode],
      }}
    >
      {options.map((option) => {
        const button = (
          <Button
            key={option.value}
            onClick={() => onValueChange(option.value)}
            variant={value === option.value ? "default" : "outline"}
            size={size}
            type="button"
            disabled={disabled}
            style={
              {
                "--active-bg":
                  storedTheme.button.primary.backgroundColor[
                    theme.palette.mode
                  ],
                "--hover-bg":
                  storedTheme.button.primary.hoverBackgroundColor[
                    theme.palette.mode
                  ],
              } as React.CSSProperties
            }
            className={cn("transition-all border-none rounded-[8px] w-full", {
              "bg-transparent text-muted-foreground": value !== option.value,
              "bg-[var(--active-bg)] text-primary-foreground":
                value === option.value,
            })}
          >
            {option.icon}
            {option.label}
          </Button>
        );

        if (option.tooltip) {
          return (
            <Tooltip key={option.value} content={option.tooltip} side="top">
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </div>
  );
}
